import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { watchedEpisodes } from '../lib/episode'
import { ladderFor, rungCrossed } from '../lib/ladder'
import { todayKey } from '../lib/datetime'
import { safeStorage } from '../lib/safeStorage'
import { supabase } from '../lib/supabase'
import { ClapboardClosed } from './ClapboardIcon'
import { useShareCard } from '../hooks/useShareCard'
import { clickable } from '../lib/a11y'

// Episodes marked across the whole list. Same arithmetic Stats17 uses.
//
// watchedEpisodes (next-to-watch aware), not countWatchedEpisodes (which wants a
// watched-THROUGH marker) — progress.episode is the NEXT episode to watch, so
// passing it raw to countWatchedEpisodes overcounted every in-progress show by
// exactly one episode (audit 2026-09-20: inflated Ladder rung, Stats17's episode
// count disagreeing with its own hours figure on the same screen, and the public
// profile's Ladder rung — the same "MyList/UpNext/ShowDetail bug class" episode.js
// already warns about at its next-to-watch section, just three call sites that
// hadn't gotten the memo).
export function episodesMarked(wl) {
  return (wl?.items || []).reduce((n, s) => n + (s ? watchedEpisodes(wl?.progress?.[s.id], s.seasons, wl?.statuses?.[s.id]) || 0 : 0), 0)
}

const EYE = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' }

// ── LadderCard: the Profile block. Level name, the count, the next rung, a bar. ──
export function LadderCard({ wl, profile, compact = false }) {
  const navigate = useNavigate()
  const [shareLabel, shareCard, sharing] = useShareCard()
  const l = useMemo(() => ladderFor(episodesMarked(wl)), [wl?.items, wl?.progress])   // eslint-disable-line react-hooks/exhaustive-deps
  const title = l.level ? l.level.name : 'Not started'
  const sub = l.next
    ? `${l.episodes.toLocaleString()} episode${l.episodes === 1 ? '' : 's'} · ${l.toGo.toLocaleString()} to ${l.next.name}`
    : `${l.episodes.toLocaleString()} episodes · top of the ladder`
  // The skin unlocks were real but invisible — nothing on Profile ever said
  // marking episodes earns anything beyond the ladder's own rung names, and the
  // theme picker itself hid a locked skin completely rather than teasing it
  // (Jason 2026-09-18: "even the themes reward is basically invisible"). This is
  // the highest-traffic surface that already shows episode progress, so the next
  // un-earned skin's real remaining count goes here rather than only in Settings.
  const nextSkin = !profile ? null : SKIN_UNLOCKS.find(u => !profile[u.flag])
  const skinToGo = nextSkin ? Math.max(0, nextSkin.minEpisodes - l.episodes) : 0
  return (
    // Was a clickable() div wrapping the Share <button> — a nested-interactive
    // axe violation (button-in-role=button) that also let Space/Enter on
    // Share fall through and navigate to /stats too (2026-09-17 accessibility
    // follow-up). Now a plain container with a real <button> around the
    // open-stats region only (icon/title/bar/sub-line); Share stays a sibling
    // in the same row so it's never nested inside another interactive element.
    <div style={{ padding: compact ? '14px 0' : '18px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
      <button onClick={() => navigate('/stats')} aria-label={`${title}. ${sub}. Open stats`} style={{ display: 'block', width: '100%', background: 'none', border: 'none', padding: 0, margin: 0, textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(var(--amber-rgb),0.12)', border: '1px solid rgba(var(--amber-rgb),0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><ClapboardClosed size={18} color="var(--amber)" /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={EYE}>Your run</div>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.15, marginTop: 2 }}>{title}</div>
          </div>
        </div>
        <div style={{ marginTop: 12, height: 4, borderRadius: 999, background: 'var(--surface)', overflow: 'hidden' }}>
          <div style={{ width: `${Math.round(l.pct * 100)}%`, height: '100%', background: 'var(--amber)', borderRadius: 999, transition: 'width 600ms var(--ease-out)' }} />
        </div>
      </button>
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>{sub}</span>
        {l.level && !compact && <button onClick={() => shareCard({ big: l.episodes, eyebrow: 'Your run', headline: l.level.name, line: `${l.episodes.toLocaleString()} episodes marked on NextEp` })} disabled={sharing} aria-busy={sharing || undefined} className="ui-hit" data-tip="Share your run" data-tip-pos="below" data-tip-align="end" style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', height: 28, padding: '0 10px', color: 'var(--text-muted)', fontFamily: 'var(--font)', fontSize: 12, cursor: 'pointer', flexShrink: 0, opacity: sharing ? 0.7 : 1 }}>{shareLabel === 'Share the card' ? 'Share' : shareLabel}</button>}
      </div>
      {nextSkin && !compact && (
        <div {...clickable(() => navigate('/settings/appearance'), { label: `${skinToGo} episodes to unlock ${nextSkin.label}. Open Appearance` })} style={{ marginTop: 2, padding: '8px 0 0', minHeight: 44, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <span aria-hidden="true" style={{ fontSize: 14 }}>🎨</span>
          <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
            {skinToGo > 0
              ? <><b style={{ color: 'var(--amber)' }}>{skinToGo.toLocaleString()}</b> more episode{skinToGo === 1 ? '' : 's'} unlocks <b style={{ color: 'var(--text)' }}>{nextSkin.label}</b></>
              : <>Unlocking <b style={{ color: 'var(--text)' }}>{nextSkin.label}</b>…</>}
          </span>
        </div>
      )}
    </div>
  )
}

// One line for other surfaces: "Regular · 15 to Season Pass".
export function ladderLine(wl) {
  const l = ladderFor(episodesMarked(wl))
  if (!l.level) return l.next ? `${l.toGo} to ${l.next.name}` : ''
  return l.next ? `${l.level.name} · ${l.toGo} to ${l.next.name}` : `${l.level.name} · top of the ladder`
}

// The ladder was the only real, already-tracked measure of "you've put real time
// into this app" — so the two gated skin tiers now hang off it instead of the
// admin-only "Grant Skin" button that was the sole unlock path (audit 2026-09-17:
// premium_skin/ultra_skin were flags nothing in the app ever set on its own —
// four themes framed as "earn it" with no way to actually earn it). Marathoner
// (100 episodes) unlocks Iridescent/Crystal/Crimson; Completist (250) unlocks
// Gilt, one clear rung above, matching its existing "rarer still" tease copy.
// Deliberately checked against the LEVEL reached, not just a fresh crossing, so
// an existing user already past 100 gets it the next time this mounts — no
// separate backfill script needed, and no risk of it drifting from the exact
// arithmetic the ladder itself already uses.
export const SKIN_UNLOCKS = [
  { minEpisodes: 100, flag: 'premium_skin', label: 'Iridescent, Crystal & Crimson', desc: '100 episodes marked. The earned skins are open in Appearance.' },
  { minEpisodes: 250, flag: 'ultra_skin',   label: 'Gilt',                          desc: '250 episodes marked. The rarest skin is open in Appearance.' },
]

// ── LadderWatcher: mounted once. Notices a rung crossing and says so, once. ──
// The baseline is remembered per user so a reload never re-announces, and the first
// observation after boot only records (a boot with 300 episodes is not "you hit
// Completist"). The moment itself is the top-rail toast, keyed so it replaces.
export function LadderWatcher({ user, profile, onProfileChange, wl }) {
  const lastRef = useRef(null)
  const markedAtRef = useRef(0)
  const skinCheckRef = useRef(new Set())
  // Only a MARK can earn a rung. The count also moves on boot (progress streams in),
  // on import and on rewind, and none of those should announce anything — a fresh
  // boot once said "Pilot" because the first observation caught a half-loaded list.
  useEffect(() => {
    const on = () => { markedAtRef.current = Date.now() }
    window.addEventListener('nextep:episode-watched', on)
    window.addEventListener('nextep:progress-marked', on)
    return () => { window.removeEventListener('nextep:episode-watched', on); window.removeEventListener('nextep:progress-marked', on) }
  }, [])
  useEffect(() => {
    if (!user?.id || !wl?.ready) return
    const key = `nextep_ladder_${user.id}`
    const now = episodesMarked(wl)
    let prev = lastRef.current
    if (prev == null) { try { const s = safeStorage.get(key); prev = s == null ? null : Number(s) } catch { prev = null } }
    lastRef.current = now
    try { safeStorage.set(key, String(now)) } catch {}
    if (prev == null || now <= prev) return
    if (Date.now() - markedAtRef.current > 8000) return   // not from a mark: boot, import, sync
    const rung = rungCrossed(prev, now)
    if (!rung) return
    const after = ladderFor(now)
    try {
      // 2026-10-11 rewards audit: the ladder is 17's one achievement track, yet a rung
      // used to get a top-rail toast while the retired 16.x badges had the full
      // medallion. The medallion is now the ladder's. `target` picks the tier ring
      // (bronze <10, silver <50, gold); the rung's episode count maps onto it naturally.
      window.dispatchEvent(new CustomEvent('nextep:achievement-unlocked', { detail: {
        id: `ladder_${rung.at}`, label: rung.name, icon: '📺', target: rung.at, eyebrow: 'New rung', route: '/profile', cta: 'See the ladder →',
        desc: `${rung.line}${after.next ? ` ${after.toGo} to ${after.next.name}.` : ''}`,
      } }))
      navigator.vibrate?.([12, 40, 18])
    } catch {}
  }, [user?.id, wl?.ready, wl?.items, wl?.progress])

  // Runs against the LEVEL reached (not a crossing) so it also catches a user who
  // was already past 100/250 before this shipped — see SKIN_UNLOCKS above. Guarded
  // per-user-per-flag by skinCheckRef so a slow network round-trip can't fire the
  // same grant twice while it's in flight.
  useEffect(() => {
    if (!user?.id || !profile || !wl?.ready) return
    const now = episodesMarked(wl)
    for (const u of SKIN_UNLOCKS) {
      if (now < u.minEpisodes) continue
      if (profile[u.flag]) continue
      const dedupeKey = `${user.id}:${u.flag}`
      if (skinCheckRef.current.has(dedupeKey)) continue
      // A refusal is remembered for the day (2026-10-11 audit): before this, a server
      // "no" was retried on every mount, forever, as a silent POST.
      const refusedKey = `nextep_skin_refused_${user.id}_${u.flag}`
      try { if (safeStorage.get(refusedKey) === todayKey()) continue } catch {}
      skinCheckRef.current.add(dedupeKey)
      ;(async () => {
        try {
          // profiles_guard_privileged (2026-09-17) blocks a plain client UPDATE to
          // premium_skin/ultra_skin outright — deliberately so (it closed a real
          // privilege-escalation hole). Rather than teach that trigger a bypass,
          // this goes through a Netlify Function using the service-role key, which
          // re-checks the threshold server-side and re-verifies the flag isn't
          // already set before granting (netlify/functions/unlock-skin.js).
          const { data: { session } } = await supabase.auth.getSession()
          if (!session) { skinCheckRef.current.delete(dedupeKey); return }
          const res = await fetch('/.netlify/functions/unlock-skin', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ episodes: now, flag: u.flag }),
          })
          const body = res.ok ? await res.json().catch(() => ({})) : {}
          if (!res.ok || !body.granted) { try { safeStorage.set(refusedKey, todayKey()) } catch {}; return }
          await onProfileChange?.()
          window.dispatchEvent(new CustomEvent('nextep:achievement-unlocked', {
            detail: { label: `${u.label} unlocked`, description: u.desc, icon: '🎨', target: u.minEpisodes },
          }))
        } catch { skinCheckRef.current.delete(dedupeKey) }
      })()
    }
  }, [user?.id, profile, wl?.ready, wl?.items, wl?.progress, onProfileChange])
  return null
}
